import * as yup from 'yup';
import { ScreeningResult } from '../../features/RenterInvite/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import dayjs from 'dayjs';
import type { CoveragePricing } from '@/shared/types';
import {
  validateMinLeaseDuration,
  validateMaxLeaseDuration,
} from '@/shared/utils/leaseDurationMonths.util';
import { isWholeNumber, MONEY_VALIDATION_MESSAGES } from '@/shared/validators';

const moneyField = yup
  .number()
  .transform((o, v) => (v ? parseFloat(v.replace(/,/g, '')) : undefined))
  .typeError('Value should be a number')
  .min(0, 'Minimum amount can be as low as $0')
  .max(50000, 'Maximum amount can be up to $50,000');

const normalizeEmail = (email: string): string => {
  if (!email) return '';
  return email.toLowerCase();
};

const applicantEmails = yup
  .array()
  .required('Applicant email is required')
  .max(6, 'Maximum number of roommates is 6')
  .of(
    yup.object().shape({
      value: yup.string().email('Invalid email').required('Email is required'),
    }),
  )
  .test('Unique', 'Applicant emails need to be unique', function (values, { createError }) {
    if (!values || !values.length) return true;
    const normalizedEmails = values.map(({ value }) => normalizeEmail(value || ''));
    const isError = new Set(normalizedEmails).size !== normalizedEmails.length;
    if (isError)
      return createError({
        path: 'applicantEmails',
        message: 'Applicant emails need to be unique (ignoring case)',
      });
    return true;
  });

const ADDRESS_REQUIRED_MSG = 'Address is required';

const createAddressSchema = () =>
  yup
    .object()
    .shape({
      monolith_building_id: yup.number().required(ADDRESS_REQUIRED_MSG),
      source: yup.string().optional().nullable(),
    })
    .typeError('Address was not found')
    .required(ADDRESS_REQUIRED_MSG);

const apartmentNumber = yup
  .string()
  .max(100, 'Apartment number is too long')
  .required('Unit number is required');

const leaseStart = yup
  .date()
  .required('Lease start date is required')
  .typeError('Lease start date is invalid')
  .min(
    dayjs(new Date().setHours(0, 0, 0, 0))
      .subtract(1, 'year')
      .add(1, 'day')
      .toDate(),
    'Date can be up to 1 year in the past',
  )
  .max(
    dayjs(new Date().setHours(23, 59, 59, 999))
      .add(1, 'year')
      .subtract(1, 'day')
      .toDate(),
    'Date can be up to 1 year in the future',
  );

const leaseEndBase = yup
  .date()
  .required('Lease end date is required')
  .typeError('Lease end date is invalid')
  .test('min-start-date', 'End date can only be after start date', function (leaseEnd) {
    if (!leaseEnd) return true;
    return dayjs(this.parent.leaseStart) < dayjs(leaseEnd);
  })
  .min(
    dayjs(new Date().setHours(0, 0, 0, 0))
      .subtract(1, 'year')
      .add(1, 'day')
      .toDate(),
    'Date can be up to 1 year in the past',
  )
  .max(
    dayjs(new Date().setHours(23, 59, 59, 999))
      .add(11, 'year')
      .subtract(1, 'day')
      .toDate(),
    'Date can be up to 1 year in the future',
  );

const createLeaseEndSchema = (useNewCoveragesOnLandlordInvite?: boolean) => {
  const MIN_LEASE_DURATION_MONTHS = 3;
  const MAX_LEASE_DURATION_MONTHS = 36;

  let schema = leaseEndBase.test(
    'min-3-months-duration',
    'Lease duration must be at least 3 months',
    function (leaseEnd) {
      if (!leaseEnd || !this.parent.leaseStart) return true;
      return validateMinLeaseDuration(this.parent.leaseStart, leaseEnd, MIN_LEASE_DURATION_MONTHS);
    },
  );

  if (useNewCoveragesOnLandlordInvite) {
    schema = schema.test(
      'max-36-months-duration',
      'Lease duration cannot exceed 36 months',
      function (leaseEnd) {
        if (!leaseEnd || !this.parent.leaseStart) return true;
        return validateMaxLeaseDuration(
          this.parent.leaseStart,
          leaseEnd,
          MAX_LEASE_DURATION_MONTHS,
        );
      },
    );
  }

  return schema;
};

export const renterSchema = (
  selfEnrolledSDRLimit?: number,
  customCoverageDisabled?: boolean,
  useNewCoveragesOnLandlordInvite?: boolean,
) =>
  yup.object().shape({
    applicantEmails,
    address: createAddressSchema(),
    apartmentNumber,
    leaseStart,
    leaseEnd: createLeaseEndSchema(useNewCoveragesOnLandlordInvite),
    grossMonthlyRent: moneyField
      .required('Total monthly rent is required')
      .test('must-be-whole-number', MONEY_VALIDATION_MESSAGES.MUST_BE_WHOLE_NUMBER, function () {
        return isWholeNumber(this.originalValue);
      }),
    prepaidRent: moneyField,
    freeRent: moneyField,
    approvalStatus: useNewCoveragesOnLandlordInvite
      ? yup
          .mixed<ScreeningResult | ''>()
          .oneOf(
            [
              '',
              ScreeningResult.Approved,
              ScreeningResult.ApprovedWithConditions,
              ScreeningResult.Denied,
            ],
            'Invalid screening result. Please choose a valid option.',
          )
          .optional()
          .notRequired()
          .default('')
      : yup.mixed<ScreeningResult | ''>().when('customCoverage', {
          is: (val: boolean) => val === false || val === undefined,
          then: (schema) =>
            schema
              .oneOf(
                [
                  ScreeningResult.Approved,
                  ScreeningResult.ApprovedWithConditions,
                  ScreeningResult.Denied,
                ],
                'Invalid screening result. Please choose a valid option.',
              )
              .required('Screening result is required'),
          otherwise: (schema) => schema.optional().notRequired(),
        }),
    monthsOfCoverage: useNewCoveragesOnLandlordInvite
      ? yup
          .number()
          .typeError('Months of coverage is required')
          .required('Months of coverage is required')
          .min(0, 'Months of coverage must be 0 or greater')
          .max(100, 'Months of coverage can be up to 100')
      : yup.number().when(['customCoverage', 'rentCoverage'], {
          is: (customCoverage: boolean, rentCoverage: RentCoverageTypes) =>
            customCoverage === true || rentCoverage === RentCoverageTypes.Customize,
          then: (schema) =>
            schema
              .typeError('Months of  coverage is required')
              .required('Months of coverage is required')
              .min(0, 'Months of coverage must be 0 or greater')
              .max(100, 'Months of coverage can be up to 100'),
          otherwise: (schema) => schema.optional().notRequired().default(undefined),
        }),

    securityDepositReplacementAmount: yup
      .number()
      .when(
        ['customCoverage', 'securityDepositCoverage', 'includeDepositCoverage', 'grossMonthlyRent'],
        (
          [customCoverage, securityDepositCoverage, includeDepositCoverage, grossMonthlyRent],
          schema,
        ) => {
          if (useNewCoveragesOnLandlordInvite) {
            return schema.default(includeDepositCoverage ? grossMonthlyRent : 0);
          }
          if (
            customCoverage === true ||
            securityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove
          ) {
            if (selfEnrolledSDRLimit) {
              return moneyField
                .required('Security deposit replacement amount is required')
                .max(
                  grossMonthlyRent * selfEnrolledSDRLimit,
                  `Amount cannot exceed ${selfEnrolledSDRLimit} month’s rent`,
                );
            }
            return moneyField.required('Security deposit replacement amount is required');
          } else {
            return schema.optional().notRequired().default(undefined);
          }
        },
      ),

    rentCoverage: customCoverageDisabled
      ? yup
          .string()
          .optional()
          .oneOf([RentCoverageTypes.CompanyDefault, RentCoverageTypes.Customize])
          .default(RentCoverageTypes.CompanyDefault)
      : yup
          .string()
          .required('Rent coverage is required')
          .oneOf([RentCoverageTypes.CompanyDefault, RentCoverageTypes.Customize]),
    securityDepositCoverage:
      customCoverageDisabled || useNewCoveragesOnLandlordInvite
        ? yup
            .string()
            .optional()
            .oneOf([SecurityDepositTypes.CompanyDefault, SecurityDepositTypes.CustomizeOrRemove])
            .default(SecurityDepositTypes.CompanyDefault)
        : yup
            .string()
            .required('Security deposit coverage is required')
            .oneOf([SecurityDepositTypes.CompanyDefault, SecurityDepositTypes.CustomizeOrRemove]),
    selectedSmbPricing: yup.mixed<CoveragePricing>().optional().notRequired(),
  });
